import React, { ReactElement, RefCallback, useCallback, useEffect, useState } from 'react';
import { FormInstance, FormProps, TableProps } from 'antd';
import { Key } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { Table } from 'common/components/antd/Table/Table';
import { PERSISTENCE_KEYS } from 'common/constants/persistence.constants';
import { getFromLocalstorage, setToLocalstorage } from 'common/utils/local-storage.utils';
import { BodyCellComponent } from './components/body-cell/body-cell.component';
import { CheckboxCellComponent } from './components/checkbox-cell/checkbox-cell.component';
import { HeaderCellComponent } from './components/header-cell/header-cell.component';
import { PermissionCellComponent } from './components/permission-cell/permission-cell.component';
import { PermissionFormComponent } from './components/permission-form/permission-form.component';
import { RoleHeaderComponent } from './components/role-header/role-header.component';
import { SSearchInput } from './components/search-input/search-input.style';
import { KEY_SEPARATOR } from './table.constants';
import { ERoleRecordKey, ERoleTableKey } from './table.enums';
import { TPermissionRecord, TScopeRecord, TTableColumn } from './table.types';
import { isScopeCheckboxKey, isScopeRecord } from './table.utils';

const SCOPE_CELL_BG = '#FAFAFA';

const components = {
  header: {
    cell: HeaderCellComponent,
  },
  body: {
    cell: BodyCellComponent,
  },
};

const searchRows = (rows: TScopeRecord[], searchText: string): TScopeRecord[] => {
  const hasSubstring = (text: string, substring: string): boolean => text.indexOf(substring) !== -1;

  const matchedChildRowsKeys = rows.flatMap((scope) =>
    scope.children
      .filter((permission) => hasSubstring(permission.title.toLowerCase(), searchText.toLowerCase()))
      .map((permission) => permission.key),
  );

  const matchedParentRowsKeys = rows.flatMap((scope) =>
    hasSubstring(scope.title.toLowerCase(), searchText.toLowerCase()) ? [scope.key] : [],
  );

  return rows
    .map((scope) => {
      if (matchedParentRowsKeys.includes(scope.key)) {
        return scope;
      }
      const filteredPermissions = scope.children.filter((permission) => matchedChildRowsKeys.includes(permission.key));

      return {
        ...scope,
        children: filteredPermissions,
      };
    })
    .filter((scope) => scope.children.length);
};

export type TPermissionTableProps = {
  rows: TScopeRecord[];
  columns: TTableColumn[];
  form: FormInstance;
  loading?: boolean;
  widths: {
    role: number;
    permission: number;
  };
  onValuesChange?: FormProps['onValuesChange'];
  onColumnMenuItemClick: (args: { key: string; column: TTableColumn }) => void;
};

const PermissionTable = ({
  rows,
  columns,
  form,
  onValuesChange,
  widths,
  loading,
  onColumnMenuItemClick,
}: TPermissionTableProps): ReactElement => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [tableContainerWidth, setTableContainerWidth] = useState(0);
  const filteredRows = searchRows(rows, searchValue);
  const initialExpandedRows = (): Key[] => {
    const storedExpandedRows = getFromLocalstorage(PERSISTENCE_KEYS.USER_ROLE_MANAGEMENT_BRANCH_ROLES, []);
    return storedExpandedRows;
  }
  const [expandedRows, setExpandedRows] = useState<Key[]>(initialExpandedRows);

  useEffect((): void => {
    setToLocalstorage(PERSISTENCE_KEYS.USER_ROLE_MANAGEMENT_BRANCH_ROLES, expandedRows);
  }, [expandedRows]);

  const handleToggleExpand = (recordKey: Key): void => {
    const newExpandedRows = expandedRows.includes(recordKey)
      ? expandedRows.filter((key: Key): boolean => key !== recordKey)
      : [...expandedRows, recordKey];
    setExpandedRows(newExpandedRows);
  }

  const expandableConfig: TableProps<TScopeRecord | TPermissionRecord>['expandable'] = {
    defaultExpandedRowKeys: expandedRows,
    showExpandColumn: true,
    expandIcon: (): null => null,
    expandedRowKeys: expandedRows,
    onExpandedRowsChange: (expandedRowKeys_): void => setExpandedRows([...expandedRowKeys_]),
  };

  const onRow: TableProps<TScopeRecord | TPermissionRecord>['onRow'] = (record) => ({
    style: {
      background: isScopeRecord(record) ? SCOPE_CELL_BG : undefined,
    },
  });

  const roleColumns: TableProps<TScopeRecord | TPermissionRecord>['columns'] = columns.map((column) => ({
    key: column.key,
    title: (
      <RoleHeaderComponent
        onMenuItemClick={({ key }): void => {
          onColumnMenuItemClick({ key, column });
        }}
      >
        {column.title}
      </RoleHeaderComponent>
    ),
    dataIndex: [ERoleTableKey.Roles, column.key],
    width: widths.role,
    render: (value, record) => <CheckboxCellComponent record={record} column={column} />,
  }));

  const searchColumn: TableProps<TScopeRecord | TPermissionRecord>['columns'] = [
    {
      title: (
        <SSearchInput
          placeholder={t('usrRoleMng.branchRoles.table.column.typePermission') as string}
          onChange={(e): void => {
            const { value } = e.currentTarget;
            setSearchValue(value);
          }}
        />
      ),
      key: 'permission',
      dataIndex: [ERoleTableKey.Title],
      width: widths.permission,
      fixed: 'left',
      render: (value, record) => <PermissionCellComponent
        value={value} record={record} expandedRows={expandedRows} onToggleExpand={handleToggleExpand}/>,
    },
  ];
  const antColumns = searchColumn.concat(roleColumns);

  const onValuesChangeInternal: FormProps['onValuesChange'] = (changedValues, values): void => {
    const keys = Object.keys(changedValues);

    const getScopeValues = (key: string, state: boolean): { [p: string]: boolean } => {
      const [columnKey, scopeRowKey] = key.split(KEY_SEPARATOR);
      const scope = filteredRows.find((scope_) => scope_.key === scopeRowKey);

      if (scope) {
        return scope.children.reduce(
          (acc, permission) => ({
            ...acc,
            [[columnKey, scope.key, permission.key].join(KEY_SEPARATOR)]: state,
          }),
          {},
        );
      }
      return {};
    };

    const getSingleValues = (key: string, state: boolean): Record<string, boolean> => {
      const [columnKey, scopeRowKey, permissionRowKey] = key.split(KEY_SEPARATOR);

      return {
        [[columnKey, scopeRowKey, permissionRowKey].join(KEY_SEPARATOR)]: state,
      };
    };

    keys.forEach((key: string): void => {
      const state = values[key];

      if (isScopeCheckboxKey(key)) {
        onValuesChange?.(getScopeValues(key, state), values);
      } else {
        onValuesChange?.(getSingleValues(key, state), values);
      }
    });
  };

  const setRef = useCallback<RefCallback<Element>>((node: Element | null): void => {
    setTableContainerWidth(node?.clientWidth || 0);
  }, []);

  // fixed table width that won't force columns to resize
  const computedTableWidth = widths.permission + (columns || []).length * widths.role;

  const containerStyle = {
    width: Math.min(computedTableWidth, tableContainerWidth),
  };

  const scroll = {
    x: computedTableWidth > tableContainerWidth ? tableContainerWidth : 'auto',
  };

  return (
    <div ref={setRef}>
      <PermissionFormComponent
        style={containerStyle}
        onValuesChange={onValuesChangeInternal}
        rows={filteredRows}
        columns={columns}
        form={form}
      >
        <Table
          onRow={onRow}
          pagination={false}
          dataSource={filteredRows}
          columns={antColumns}
          loading={loading}
          indentSize={0}
          bordered
          scroll={scroll}
          components={components}
          rowKey={ERoleRecordKey.Key}
          expandable={expandableConfig}
        />
      </PermissionFormComponent>
    </div>
  );
};

export default PermissionTable;
